import { forwardRef } from "react";
import { TextField as MuiTextField, InputAdornment } from "@mui/material";

import type { TextFieldProps as MuiTextFieldProps } from "@mui/material";
import type { ReactNode } from "react";
import type { FormFieldSize } from "../../types";

export interface TextFieldProps extends Omit<
  MuiTextFieldProps,
  "variant" | "size" | "color" | "multiline" | "rows" | "maxRows" | "minRows"
> {
  /** Field size. Default: `"sm"` */
  size?: FormFieldSize;
  /** Element rendered before the input value. */
  startAdornment?: ReactNode;
  /** Element rendered after the input value. */
  endAdornment?: ReactNode;
  /** Root test id. Default: `"ds-textfield"` */
  "data-testid"?: string;
}

const sizeMap: Record<FormFieldSize, MuiTextFieldProps["size"]> = {
  sm: "small",
  md: "medium",
};

/** Constrains MUI TextField to the outlined Centro input and the sm/md size set. */
export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      size = "sm",
      startAdornment,
      endAdornment,
      slotProps,
      "data-testid": dataTestId = "ds-textfield",
      ...rest
    },
    ref,
  ) => {
    const inputSlotProps =
      startAdornment || endAdornment
        ? {
            ...slotProps?.input,
            ...(startAdornment && {
              startAdornment: (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ),
            }),
            ...(endAdornment && {
              endAdornment: (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ),
            }),
          }
        : slotProps?.input;

    return (
      // rest spreads first so the locked props below can't be overridden at
      // runtime (e.g. by JS callers passing variant/size/color).
      <MuiTextField
        {...rest}
        ref={ref}
        data-testid={dataTestId}
        variant="outlined"
        color="brandSupporting"
        size={sizeMap[size]}
        slotProps={{ ...slotProps, input: inputSlotProps }}
      />
    );
  },
);

TextField.displayName = "TextField";
